import React, { useState, useEffect } from "react";
import { useTheme } from "../hooks/useTheme";
import AppSidebar from "./AppSidebar";
import { useNavigate, useLocation } from "react-router-dom";
import MetaHead from "./MetaHead";
import { useTranslation } from "react-i18next";
import { FormInput, FormTextarea, FormButton } from "./FormComponents";
import { getTodayString } from "../utils/dateUtils";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { createNote } from "../store/slices/notesSlice";
import { safeToUUID } from "../utils/uuidUtils";
import { trackPageVisit, trackUserInteraction } from "../utils/analytics";
import { useToastContext } from "./ToastContext";

const AddNote: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.user.profile);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { addToast } = useToastContext();
  const { isDark, setTheme } = useTheme();
  const initials = currentUser?.name
    ? currentUser.name
        .split(" ")
        .map((w: string) => w.charAt(0).toUpperCase())
        .slice(0, 2)
        .join("")
    : (currentUser?.username?.charAt(0).toUpperCase() ?? "U");

  const handleThemeToggle = () => {
    setTheme(isDark ? "light" : "dark");
  };

  const isNavActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const mobileNavLinks = [
    { path: "/app", emoji: "📅", label: t("nav.calendar") },
    { path: "/habits", emoji: "🔥", label: t("nav.habits") },
    { path: "/todos", emoji: "✅", label: t("nav.todos") },
    { path: "/trackers", emoji: "📊", label: t("nav.trackers") },
    { path: "/notes", emoji: "📝", label: t("nav.notes") },
  ];

  // Get date from URL query parameter
  const queryParams = new URLSearchParams(location.search);
  const prefilledDate = queryParams.get("date");

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    date: prefilledDate || getTodayString(),
    tags: "",
    visibility: "private" as "private" | "public",
  });

  useEffect(() => {
    trackPageVisit("add_note");
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.content.trim()) {
      return;
    }

    setLoading(true);

    try {
      // Process tags
      const tagsArray = formData.tags
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);

      // Dispatch createNote action to Redux store
      const result = await dispatch(
        createNote({
          userId: currentUser
            ? safeToUUID(currentUser.id)
            : safeToUUID("user-123e4567-e89b-12d3-a456-426614174000"), // Fallback to demo user
          title: formData.title.trim(),
          content: formData.content.trim(),
          date: formData.date,
          tags: tagsArray.length > 0 ? tagsArray : undefined,
          visibility: formData.visibility,
        }),
      ).unwrap();

      // Show toast if points earned
      if (result.pointsEarned > 0) {
        addToast(
          t("toast.pointsEarned", { points: result.pointsEarned }),
          "success",
        );
      }

      // Track note creation event
      trackUserInteraction(
        "create_note",
        "note_management",
        result?.id || "unknown",
      );

      // Navigate to notes page
      navigate("/notes");
    } catch (error) {
      console.error("Error saving note:", error);
      // TODO: Show error message to user
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/notes");
  };

  const trimmedTitle = formData.title.trim();
  const trimmedContent = formData.content.trim();
  const isSubmitDisabled = !trimmedTitle || !trimmedContent;

  return (
    <>
      <MetaHead
        title={t("addNote.title")}
        description={t("addNote.description") as string}
        keywords={t("addNote.keywords") as string}
      />
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex flex-col lg:flex-row lg:max-w-6xl lg:mx-auto">
        {/* DESKTOP SIDEBAR */}
        <AppSidebar showQuickActions={false} />

        {/* MAIN CONTENT */}
        <main className="flex-1 flex flex-col bg-[#f7f6f3] dark:bg-[#0f0f12] min-h-screen lg:min-h-0 pb-24 lg:pb-8">
          {/* MOBILE TOPBAR */}
          <header className="lg:hidden flex items-center justify-between px-4 py-3.5 border-b border-black/[0.07] dark:border-white/[0.07] sticky top-0 bg-[#f7f6f3] dark:bg-[#0f0f12] z-20">
            <button
              onClick={handleCancel}
              aria-label="Go back"
              className="tap-scale w-8 h-8 rounded-[9px] bg-[#3b5cde]/20 border border-[#3b5cde]/30 flex items-center justify-center transition-all hover:bg-[#3b5cde]/30"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#7b9fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <p className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white leading-none">
              {t("addNote.headerTitle")}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleThemeToggle}
                aria-label={
                  isDark
                    ? (t("theme.switchToLight") as string)
                    : (t("theme.switchToDark") as string)
                }
                className="w-8 h-8 rounded-[9px] border border-black/10 dark:border-white/10 bg-black/[0.04] dark:bg-white/[0.05] text-gray-500 dark:text-white/50 text-[19px] flex items-center justify-center cursor-pointer transition-all hover:bg-black/10 dark:hover:bg-white/10"
              >
                {isDark ? "☀️" : "🌙"}
              </button>
              <button
                onClick={() => navigate("/profile")}
                aria-label="Go to profile"
                className="w-8 h-8 rounded-full bg-[#3b5cde] flex items-center justify-center text-[16px] font-semibold text-white"
              >
                {initials}
              </button>
            </div>
          </header>

          {/* DESKTOP PAGE HEADER */}
          <div className="hidden lg:flex items-center gap-4 px-6 py-5 border-b border-black/[0.07] dark:border-white/[0.07]">
            <button
              onClick={handleCancel}
              aria-label="Go back"
              className="tap-scale w-9 h-9 rounded-xl bg-[#3b5cde]/15 border border-[#3b5cde]/25 flex items-center justify-center transition-all hover:bg-[#3b5cde]/25"
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#7b9fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <h1 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
              {t("addNote.headerTitle")}
            </h1>
          </div>

          {/* SCROLLABLE CONTENT */}
          <div className="flex-1 overflow-y-auto scrollbar-hide px-3 lg:px-6 py-4">
            <div className="max-w-4xl mx-auto pb-8 space-y-6">
              <div className="glass-card p-5 lg:p-6 rounded-xl border border-[#22c67a]/20 dark:border-[#22c67a]/30 bg-gradient-to-br from-[#22c67a]/[0.06] via-transparent to-[#3b5cde]/[0.04] dark:from-[#22c67a]/[0.09] dark:to-[#3b5cde]/[0.06]">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#22c67a]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg
                        className="w-5 h-5 text-[#22c67a]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 7h10M7 12h7m-7 5h10M6 3h12a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V5a2 2 0 012-2z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h2 className="text-[19px] font-semibold text-neutral-900 dark:text-neutral-100 mb-1">
                        {t("addNote.createNewNote")}
                      </h2>
                      <p className="text-[17px] text-neutral-600 dark:text-neutral-400 leading-relaxed">
                        {t("addNote.noteDescription")}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 lg:min-w-[260px]">
                    <div className="rounded-xl border border-black/[0.07] dark:border-white/[0.08] bg-white/80 dark:bg-white/[0.05] px-3 py-3">
                      <p className="text-[14px] uppercase tracking-[0.18em] text-neutral-500 dark:text-white/35 mb-1">
                        {t("addNote.date")}
                      </p>
                      <p className="text-[17px] font-medium text-neutral-900 dark:text-white">
                        {formData.date}
                      </p>
                    </div>
                    <div className="rounded-xl border border-black/[0.07] dark:border-white/[0.08] bg-white/80 dark:bg-white/[0.05] px-3 py-3">
                      <p className="text-[14px] uppercase tracking-[0.18em] text-neutral-500 dark:text-white/35 mb-1">
                        {t("addNote.visibility")}
                      </p>
                      <p className="text-[17px] font-medium text-neutral-900 dark:text-white">
                        {formData.visibility === "private"
                          ? t("addNote.visibilityPrivate")
                          : t("addNote.visibilityPublic")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label
                      htmlFor="title"
                      className="block text-[17px] font-medium text-neutral-900 dark:text-neutral-100 mb-2.5"
                    >
                      {t("addNote.noteTitle")}
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <FormInput
                      id="title"
                      label=""
                      value={formData.title}
                      onChange={(value) =>
                        setFormData({ ...formData, title: value })
                      }
                      placeholder={t("addNote.titlePlaceholder") as string}
                    />
                    <p className="text-[15px] text-neutral-500 dark:text-neutral-400 -mt-3">
                      {formData.title.length}/120
                    </p>
                  </div>

                  <div>
                    <label
                      htmlFor="content"
                      className="block text-[17px] font-medium text-neutral-900 dark:text-neutral-100 mb-2.5"
                    >
                      {t("addNote.noteContent")}
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <FormTextarea
                      id="content"
                      label=""
                      value={formData.content}
                      onChange={(value) =>
                        setFormData({ ...formData, content: value })
                      }
                      placeholder={t("addNote.contentPlaceholder") as string}
                      rows={9}
                    />
                    <p className="text-[15px] text-neutral-500 dark:text-neutral-400 -mt-3">
                      {formData.content.length}/2000
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4">
                    <div className="rounded-xl border border-black/[0.07] dark:border-white/[0.08] bg-white/60 dark:bg-white/[0.03] p-4">
                      <label
                        htmlFor="date"
                        className="block text-[17px] font-medium text-neutral-900 dark:text-neutral-100 mb-2.5"
                      >
                        {t("addNote.date")}
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <FormInput
                        id="date"
                        label=""
                        type="date"
                        value={formData.date}
                        onChange={(value) =>
                          setFormData({ ...formData, date: value })
                        }
                      />
                      <p className="text-[16px] text-neutral-600 dark:text-neutral-400 -mt-3">
                        {t("addNote.dateHelper")}
                      </p>
                    </div>

                    <div className="rounded-xl border border-black/[0.07] dark:border-white/[0.08] bg-white/60 dark:bg-white/[0.03] p-4">
                      <label
                        htmlFor="visibility"
                        className="block text-[17px] font-medium text-neutral-900 dark:text-neutral-100 mb-2.5"
                      >
                        {t("addNote.visibility")}
                      </label>
                      <select
                        id="visibility"
                        value={formData.visibility}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            visibility: e.target.value as "private" | "public",
                          })
                        }
                        className="w-full px-4 py-3.5 rounded-xl border border-black/[0.1] bg-black/[0.04] text-neutral-900 focus:outline-none focus:border-[#22c67a] transition-colors duration-150 dark:border-white/[0.1] dark:bg-white/[0.06] dark:text-neutral-100"
                      >
                        <option value="private">
                          {t("addNote.visibilityPrivate")}
                        </option>
                        <option value="public">
                          {t("addNote.visibilityPublic")}
                        </option>
                      </select>
                      <div className="mt-3 rounded-lg border border-[#22c67a]/15 dark:border-[#22c67a]/20 bg-[#22c67a]/[0.06] dark:bg-[#22c67a]/[0.08] px-3 py-3">
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 rounded-full bg-[#22c67a] mt-1.5 flex-shrink-0"></div>
                          <p className="text-[16px] text-neutral-700 dark:text-neutral-300">
                            {formData.visibility === "private"
                              ? t("addNote.visibilityPrivateDesc")
                              : t("addNote.visibilityPublicDesc")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="tags"
                      className="block text-[17px] font-medium text-neutral-900 dark:text-neutral-100 mb-2.5"
                    >
                      {t("addNote.tags")}
                    </label>
                    <FormInput
                      id="tags"
                      label=""
                      value={formData.tags}
                      onChange={(value) =>
                        setFormData({ ...formData, tags: value })
                      }
                      placeholder={t("addNote.tagsPlaceholder") as string}
                    />
                    <p className="text-[16px] text-neutral-600 dark:text-neutral-400 -mt-3">
                      {t("addNote.tagsHelper")}
                    </p>
                  </div>

                  <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                    <div className="flex-1">
                      <FormButton
                        type="button"
                        variant="secondary"
                        onClick={handleCancel}
                        disabled={loading}
                        size="lg"
                      >
                        {t("addNote.cancel")}
                      </FormButton>
                    </div>

                    <div className="flex-1">
                      <FormButton
                        type="submit"
                        variant="primary"
                        loading={loading}
                        disabled={isSubmitDisabled}
                        size="lg"
                        className="bg-[#22c67a] border-[#22c67a] text-[#052e1b] focus:ring-[#22c67a]"
                      >
                        {loading ? t("addNote.saving") : t("addNote.saveNote")}
                      </FormButton>
                    </div>
                  </div>
                </form>
              </div>

              <div className="glass-card rounded-xl p-5 lg:p-6">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#22c67a]/[0.15] flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-[#22c67a]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-[17px] font-semibold text-neutral-900 dark:text-neutral-100 mb-1">
                      {t("addNote.helpTitle")}
                    </h3>
                    <p className="text-[16px] text-neutral-600 dark:text-neutral-400 leading-relaxed">
                      {t("addNote.helpText")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* MOBILE BOTTOM NAV */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#f7f6f3]/95 dark:bg-[#0f0f12]/95 backdrop-blur-sm border-t border-black/[0.08] dark:border-white/[0.08]">
          <div className="flex items-center justify-around px-2 pt-2 pb-2">
            {mobileNavLinks.map(({ path, emoji, label }) => {
              const active = isNavActive(path);
              return (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  className="flex flex-col items-center gap-1 cursor-pointer"
                  style={{ color: active ? "#f5a623" : undefined }}
                >
                  <span className="text-[22px]">{emoji}</span>
                  <span
                    className={`text-[13px] font-medium${active ? "" : " text-gray-400 dark:text-white/25"}`}
                    style={active ? { color: "#f5a623" } : undefined}
                  >
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>
      </div>
    </>
  );
};

export default AddNote;
